import type { ErrorCode } from './errors.js';

/**
 * Cosa fa l'app dopo un errore di riproduzione (06-RIPRODUZIONE §6).
 *
 * ## Perché è una regola di dominio e non una riga dentro la schermata
 *
 * «Quante volte si riprova» è la differenza fra un'app che si fa capire e una che dice sempre
 * «errore di riproduzione». Un decoder che rifiuta un flusso e una rete assente sono guasti diversi
 * con rimedi diversi: il primo non migliorerà mai riprovando — è la macchina, non il canale — la
 * seconda si risolve da sola appena la rete torna. Scriverla dentro una schermata significa
 * riscriverla in ognuna, e divergere alla seconda.
 *
 * E si prova senza un browser, che è l'altra metà del motivo: una politica di ripetizione sbagliata
 * si manifesta come «l'app insiste» o «l'app si arrende subito», due cose che nessuno segnala come
 * difetto e che qui invece cadono con una asserzione.
 *
 * ## Il conto è in tentativi, ma l'attesa è in millisecondi
 *
 * `attempts` dice **quante volte** riprovare; `backoffMs` dice **quando**. Sono separati perché
 * l'errore già fatto su questa piattaforma è misurare un'attesa in «controlli»: «riprovo dieci
 * volte» è un tempo che dipende da quanto è lenta la rete, cioè non è un tempo. Qui il numero di
 * tentativi non porta nessuna promessa sulla durata, e la durata la dice l'elenco delle attese.
 */

export type RetryKind =
  /** Non si riprova: riprovare non cambierebbe niente, e insistere occupa una connessione. */
  | 'never'
  /** Si riprova da soli, un numero chiuso di volte, con attese crescenti. */
  | 'automatic'
  /** Non si riprova a orologio: si aspetta un segnale dall'apparecchio (la rete che torna). */
  | 'when-online'
  /** Non si riprova qui: si torna all'accesso, perché la credenziale non vale più. */
  | 'back-to-login';

export interface RetryPolicy {
  readonly kind: RetryKind;
  /** Quanti tentativi automatici, oltre al primo. Zero per tutto ciò che non si riprova. */
  readonly attempts: number;
  /** Le attese fra un tentativo e il successivo, in millisecondi. Lunga quanto `attempts`. */
  readonly backoffMs: ReadonlyArray<number>;
}

const NEVER: RetryPolicy = { kind: 'never', attempts: 0, backoffMs: [] };

/**
 * Le politiche, una per codice, nell'ordine della tabella di 06-RIPRODUZIONE §6.
 *
 * Non c'è un valore predefinito generoso: ciò che non è elencato **non si riprova**. Un ripiego che
 * riprova per un codice ignoto è un'app che insiste su un guasto che nessuno ha capito, e il costo
 * lo paga la linea del cliente — che di connessioni ne ha due.
 */
const POLICIES: Partial<Record<ErrorCode, RetryPolicy>> = {
  // Il limite di connessioni non passa da sé: passa quando qualcuno spegne un altro apparecchio.
  'line.max_connections_reached': NEVER,

  /*
    Scaduta, disabilitata, bandita: non sono guasti, sono condizioni della linea. Si torna
    all'accesso perché è lì che si rimedia — e riprovare direbbe al cliente che forse è la rete,
    mandandolo a riavviare il router per un abbonamento finito.

    Sono gli stessi codici su cui `sendsBackToLogin` decide di dimenticare le credenziali: le due
    funzioni rispondono a domande diverse — «riprovo?» e «cancello?» — e devono concordare.
  */
  'line.expired': { kind: 'back-to-login', attempts: 0, backoffMs: [] },
  'line.disabled': { kind: 'back-to-login', attempts: 0, backoffMs: [] },
  'line.banned': { kind: 'back-to-login', attempts: 0, backoffMs: [] },
  'auth.invalid_token': { kind: 'back-to-login', attempts: 0, backoffMs: [] },
  'auth.invalid_credentials': { kind: 'back-to-login', attempts: 0, backoffMs: [] },
  /*
    Questi due li ha trovati la prova incrociata, non io: li avevo dimenticati, e senza quel
    confronto sarebbero rimasti `never`.

    `auth.missing_credentials` è un difetto dell'app — ha chiamato senza credenziali — ma la via
    d'uscita è la stessa: riportare a una schermata dove qualcuno le rimette. `auth.wrong_gateway` è
    una credenziale valida presentata alla porta sbagliata, e riprovare sulla stessa porta non la
    farà diventare giusta.
  */
  'auth.missing_credentials': { kind: 'back-to-login', attempts: 0, backoffMs: [] },
  'auth.wrong_gateway': { kind: 'back-to-login', attempts: 0, backoffMs: [] },

  /*
    Il canale non è partito entro il tempo: **un** tentativo automatico, poi ci si ferma.

    Uno e non tre, e la ragione è che il primo tentativo ha già atteso quindici secondi. Tre
    tentativi sarebbero quarantacinque secondi davanti a uno schermo nero, cioè un'app rotta con le
    migliori intenzioni. I due secondi di attesa servono a non ripartire nello stesso istante in cui
    il transcodificatore sta ancora morendo.
  */
  'stream.start_timeout': { kind: 'automatic', attempts: 1, backoffMs: [2000] },

  /*
    La sorgente è caduta **mentre si guardava**: tre tentativi a intervalli crescenti.

    Qui si è più insistenti che all'avvio, ed è voluto: chi sta guardando da venti minuti ha già
    dimostrato che il canale funziona, quindi l'ipotesi più probabile è un singhiozzo. All'avvio no.
  */
  'stream.source_dropped': { kind: 'automatic', attempts: 3, backoffMs: [1000, 3000, 8000] },

  // Il decoder: è la macchina, non il canale. Su un box con WebView vecchia è il guasto più
  // probabile di tutti, e riprovare è l'unica cosa che sicuramente non serve.
  'stream.decoder_unsupported': NEVER,

  // Un canale spento o in manutenzione non si accende riprovando: lo riaccende un operatore.
  'stream.not_available': NEVER,
  // E un canale che non si è comprato non entra nell'abbonamento insistendo.
  'stream.not_in_subscription': NEVER,

  // La rete: non si riprova a orologio, si aspetta il segnale. Riprovare ogni due secondi mentre il
  // wifi è spento consuma batteria per non scoprire niente.
  'device.offline': { kind: 'when-online', attempts: 0, backoffMs: [] },

  // Un tempo scaduto sulla richiesta è l'unico caso ritentabile fra quelli dell'apparecchio.
  'device.request_timeout': { kind: 'automatic', attempts: 2, backoffMs: [1000, 3000] },

  // Il catch-up che non c'è per quel canale non comparirà riprovando.
  'catchup.not_available': NEVER,
};

/**
 * La politica per un codice. Ciò che non è elencato non si riprova.
 *
 * Restituisce sempre qualcosa: un chiamante che dovesse gestire l'assenza finirebbe per inventarsi
 * un ripiego suo, e sarebbe di nuovo una regola scritta in due posti.
 */
export function retryPolicyFor(code: string): RetryPolicy {
  return POLICIES[code as ErrorCode] ?? NEVER;
}

/**
 * Quanto aspettare prima del tentativo numero `attempt` (il primo tentativo automatico è `1`).
 *
 * `null` quando non c'è un altro tentativo: è la risposta a «devo riprovare?», e distinguerla da
 * «aspetta zero millisecondi» evita il ciclo stretto che nasce quando un ripiego restituisce 0.
 */
export function retryDelayMs(code: string, attempt: number): number | null {
  const policy = retryPolicyFor(code);
  if (policy.kind !== 'automatic') return null;
  if (attempt < 1 || attempt > policy.attempts) return null;
  return policy.backoffMs[attempt - 1] ?? null;
}
